// Global Instructions Rule Applied!

import { z } from 'zod'

// Shapes served by the document-reports customization endpoints
// (backend/src/app_modules/document_reports — customization-context and the
// PUT save route). Drawer-local state, not Redux, but every API response is
// still validated before use.
//
// Two customization VARIANTS exist, discriminated by the server-emitted
// `variant` field (single-sourced from the backend's CUSTOMIZATIONS registry
// rather than a report-code switch here):
//   'performance_highlights'  Investor Performance Report — period highlight
//                             slots + key-driver spotlight picks.
//   'feedback_exit_outlook'   Investor Feedback Report — watch-flag slots +
//                             per-company exit-outlook overrides.

// ---------------------------------------------------------------------------
// performance_highlights

/** One saved key-driver pick: null chip/blurb = generated for that card. */
export const CustomKeyDriverSchema = z
  .object({
    account_id: z.string(),
    chip: z.string().nullish(),
    blurb: z.string().nullish()
  })
  .strict()

/** The stored customization blob — the builder's canonical shape. */
export const CustomizationDataSchema = z
  .object({
    // 4 fixed slots; null = the generated sentence for that slot.
    highlights: z.array(z.string().nullable()).default([]),
    // null/absent = generated picks.
    key_drivers: z.array(CustomKeyDriverSchema).nullish()
  })
  .strict()

/** The saved row as the read RPC returns it (null when nothing is stored). */
export const SavedCustomizationSchema = z
  .object({
    id: z.string(),
    data: CustomizationDataSchema,
    date_modified: z.string(),
    modified_by_name: z.string().nullish()
  })
  .strict()

/** One key-driver pool entry: any company in the run, with its period move
 *  and the generated chip/blurb the drawer shows as placeholders. */
export const KeyDriverPoolEntrySchema = z
  .object({
    account_id: z.string(),
    name: z.string(),
    delta: z.number(),
    delta_label: z.string(),
    chip: z.string(),
    blurb: z.string()
  })
  .strict()

/** The approved LP period highlight set behind the drawer's highlight
 *  baseline (LP agent programme Phase 2), or null when the baseline is the
 *  generated sentences. */
export const ApprovedHighlightSetRefSchema = z
  .object({
    id: z.string(),
    version: z.number(),
    approved_at: z.string().nullish()
  })
  .strict()

export const PerformanceCustomizationContextSchema = z
  .object({
    variant: z.literal('performance_highlights'),
    investor_account_id: z.string(),
    approved_set: ApprovedHighlightSetRefSchema.nullish(),
    period: z
      .object({
        start: z.string(),
        end: z.string(),
        label: z.string()
      })
      .strict(),
    // Char budgets — layout-derived server constants, never re-hardcoded here.
    limits: z
      .object({
        highlight_slots: z.number(),
        highlight_max: z.number(),
        cards: z.number(),
        blurb_max: z.number(),
        chip_max: z.number()
      })
      .strict(),
    generated: z
      .object({
        // Editable `**bold**` markup strings, one per BASELINE bullet: the
        // approved set's short text where one exists (approved_set is set),
        // else the generated sentence.
        highlights: z.array(z.string()),
        key_drivers: z.array(
          z
            .object({
              account_id: z.string().nullish(),
              name: z.string(),
              chip: z.string(),
              blurb: z.string()
            })
            .strict()
        )
      })
      .strict(),
    pool: z.array(KeyDriverPoolEntrySchema),
    saved: SavedCustomizationSchema.nullish()
  })
  .strict()

// ---------------------------------------------------------------------------
// feedback_exit_outlook

/** One saved exit-outlook override: signal and/or note per company. */
export const FeedbackOutlookEntrySchema = z
  .object({
    account_id: z.string(),
    signal: z.string().nullish(),
    note: z.string().nullish()
  })
  .strict()

/** The stored customization blob — the feedback builder's canonical shape. */
export const FeedbackCustomizationDataSchema = z
  .object({
    // Fixed slots; null = the generated flag for that slot.
    flags: z.array(z.string().nullable()).default([]),
    // null/absent = fully derived outlooks.
    exit_outlook: z.array(FeedbackOutlookEntrySchema).nullish()
  })
  .strict()

export const FeedbackSavedCustomizationSchema = z
  .object({
    id: z.string(),
    data: FeedbackCustomizationDataSchema,
    date_modified: z.string(),
    modified_by_name: z.string().nullish()
  })
  .strict()

/** One outlook pool entry: every active company, largest NAV first, with the
 *  derived signal the deck would show and an evidence caption. */
export const OutlookPoolEntrySchema = z
  .object({
    account_id: z.string(),
    name: z.string(),
    nav_label: z.string(),
    derived_signal: z.string().nullish(),
    evidence: z.string()
  })
  .strict()

export const FeedbackCustomizationContextSchema = z
  .object({
    variant: z.literal('feedback_exit_outlook'),
    fund_id: z.string(),
    period: z
      .object({
        start: z.string(),
        end: z.string(),
        label: z.string()
      })
      .strict(),
    limits: z
      .object({
        flag_slots: z.number(),
        flag_max: z.number(),
        note_max: z.number(),
        // The exit-signal vocabulary the Select offers — builder-owned.
        signals: z.array(z.object({ value: z.string(), label: z.string() }).strict())
      })
      .strict(),
    generated: z
      .object({
        // Editable `**bold**` markup strings, one per generated flag.
        flags: z.array(z.string()),
        exit_outlook: z.array(
          z
            .object({
              account_id: z.string().nullish(),
              name: z.string(),
              signal: z.string().nullish()
            })
            .strict()
        )
      })
      .strict(),
    pool: z.array(OutlookPoolEntrySchema),
    saved: FeedbackSavedCustomizationSchema.nullish()
  })
  .strict()

// ---------------------------------------------------------------------------
// the union the endpoints are validated against

export const CustomizationContextSchema = z.discriminatedUnion('variant', [
  PerformanceCustomizationContextSchema,
  FeedbackCustomizationContextSchema
])

/** A saved row of either shape — the save route's response. */
export const AnySavedCustomizationSchema = z.union([
  SavedCustomizationSchema,
  FeedbackSavedCustomizationSchema
])

export type CustomKeyDriver = z.infer<typeof CustomKeyDriverSchema>
export type CustomizationData = z.infer<typeof CustomizationDataSchema>
export type SavedCustomization = z.infer<typeof SavedCustomizationSchema>
export type KeyDriverPoolEntry = z.infer<typeof KeyDriverPoolEntrySchema>
export type ApprovedHighlightSetRef = z.infer<typeof ApprovedHighlightSetRefSchema>
export type PerformanceCustomizationContext = z.infer<
  typeof PerformanceCustomizationContextSchema
>
export type FeedbackOutlookEntry = z.infer<typeof FeedbackOutlookEntrySchema>
export type FeedbackCustomizationData = z.infer<typeof FeedbackCustomizationDataSchema>
export type FeedbackSavedCustomization = z.infer<typeof FeedbackSavedCustomizationSchema>
export type OutlookPoolEntry = z.infer<typeof OutlookPoolEntrySchema>
export type FeedbackCustomizationContext = z.infer<typeof FeedbackCustomizationContextSchema>
export type CustomizationContext = z.infer<typeof CustomizationContextSchema>
export type AnySavedCustomization = z.infer<typeof AnySavedCustomizationSchema>
